import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import type { FollowedTopicKind } from "@connect/shared";
import { useApi } from "@/lib/api";
import { useTrack } from "@/lib/track";
import { cn } from "@/lib/cn";

interface Props {
  topicKind: FollowedTopicKind;
  topicValue: string;
  isFollowing: boolean;
  /** Compact style for in-card use; default is a labeled pill. */
  variant?: "pill" | "icon";
}

/**
 * Generic follow toggle. Works for opportunity_kind / category / geo / entity.
 * In `pill` variant, shows the topic_value as a quiet pill — useful for
 * follows-management lists. In `icon` variant, shows just a + or × — used in
 * card action rows.
 */
export function FollowButton({
  topicKind,
  topicValue,
  isFollowing,
  variant = "icon",
}: Props) {
  const api = useApi();
  const qc = useQueryClient();
  const track = useTrack();

  const toggle = useMutation({
    mutationFn: () =>
      isFollowing
        ? api.del(
            `/opportunities/follows/${encodeURIComponent(topicKind)}/${encodeURIComponent(topicValue)}`,
          )
        : api.post("/opportunities/follows", {
            topic_kind: topicKind,
            topic_value: topicValue,
          }),
    onSuccess: () => {
      if (!isFollowing) track("follow.add", { topic_kind: topicKind });
      qc.invalidateQueries({ queryKey: ["opp-follows"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
      qc.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });

  if (variant === "pill") {
    return (
      <button
        type="button"
        onClick={() => toggle.mutate()}
        disabled={toggle.isPending}
        className={cn(
          "group inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs",
          "border transition-colors disabled:opacity-50",
          "border-veil/60 text-fog hover:border-veil hover:bg-veil/30",
        )}
        title={`Unfollow ${topicValue}`}
        aria-label={`Unfollow ${topicValue}`}
      >
        <span className="tracking-wide">{topicValue}</span>
        <X
          className="size-3 text-muted group-hover:text-whisper transition-colors"
          strokeWidth={1.5}
        />
      </button>
    );
  }

  const Icon = isFollowing ? X : Plus;
  return (
    <button
      type="button"
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      title={isFollowing ? `Unfollow ${topicValue}` : `Follow ${topicValue}`}
      aria-label={isFollowing ? "Unfollow" : "Follow"}
      className={cn(
        "p-1.5 rounded-md transition-colors disabled:opacity-40",
        isFollowing ? "text-gold-glow" : "text-whisper hover:text-fog",
        "hover:bg-veil/40",
      )}
    >
      <Icon className="size-4" strokeWidth={1.5} />
    </button>
  );
}
